import { useCallback, useMemo, useRef } from 'react';
import { Html } from '@react-three/drei';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { type Memory } from '../data/locations';
import { usePinHighlight } from '../state/pinHighlight';
import { PIN_RADIUS, isVisibleFromCamera, latLngToVec3, pinFacesCamera } from './geo';

const HIT_GEO = new THREE.SphereGeometry(0.1, 8, 8);

const _cam = new THREE.Vector3();

function Marker({
  city,
  selected,
  hovered,
  ringed,
  dimmed,
  onSelect,
}: {
  city: Memory;
  selected: boolean;
  hovered: boolean;
  ringed: boolean;
  dimmed: boolean;
  onSelect: (slug: string) => void;
}) {
  const gl = useThree((s) => s.gl);
  const down = useRef<{ x: number; y: number } | null>(null);
  const hot = useRef(false);
  const domRef = useRef<HTMLDivElement>(null);
  const owner = `pin:${city.id}`;

  const position = useMemo(() => latLngToVec3(city.lat, city.lng, PIN_RADIUS), [city.lat, city.lng]);

  // Hide the pin once it rotates past the horizon. The DOM sits above the canvas
  // so the globe can't occlude it for us — but three.js calls onBeforeRender on
  // every drawn frame (unlike R3F's useFrame, which this setup drops), so this is
  // a reliable per-frame hook.
  const attachHit = useCallback(
    (mesh: THREE.Mesh | null) => {
      if (!mesh) return;
      mesh.onBeforeRender = (_r, _s, camera) => {
        const dom = domRef.current;
        if (!dom) return;
        camera.getWorldPosition(_cam);
        dom.style.display = pinFacesCamera(position, _cam) ? '' : 'none';
      };
    },
    [position],
  );

  return (
    <group position={position}>
      <mesh
        ref={attachHit}
        geometry={HIT_GEO}
        frustumCulled={false}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          if (e.nativeEvent.pointerType === 'touch') return;
          if (!isVisibleFromCamera(position, e.camera.position)) return;
          e.stopPropagation();
          hot.current = true;
          usePinHighlight.getState().activate(owner, city.id, 'pin');
          gl.domElement.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          if (!hot.current) return;
          hot.current = false;
          usePinHighlight.getState().release(owner);
          gl.domElement.style.cursor = 'auto';
        }}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          down.current = { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY };
        }}
        onPointerCancel={() => {
          down.current = null;
        }}
        onPointerUp={(e: ThreeEvent<PointerEvent>) => {
          const d = down.current;
          down.current = null;
          if (!d) return;
          if (Math.hypot(e.nativeEvent.clientX - d.x, e.nativeEvent.clientY - d.y) > 6) return;
          if (!isVisibleFromCamera(position, e.camera.position)) return;
          e.stopPropagation();
          onSelect(city.id);
        }}
      >
        <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
      </mesh>

      <Html
        position={[0, 0, 0]}
        style={{ pointerEvents: 'none' }}
        zIndexRange={[20, 0]}
        wrapperClass={`pin-wrap${selected ? ' is-selected' : hovered ? ' is-hovered' : ''}${ringed ? ' is-ringed' : ''}`}
      >
        <div ref={domRef} className="pin-anchor">
          {(selected || hovered) && <div className="pin-label">{city.name}</div>}
          <div
            className={`memory-pin${selected ? ' is-selected' : ''}${hovered ? ' is-hovered' : ''}${ringed ? ' is-ringed' : ''}${dimmed && !hovered ? ' is-dimmed' : ''}`}
          >
            <span className="memory-pin__ring" aria-hidden="true" />
            <svg viewBox="0 0 32 42" className="memory-pin__svg" aria-hidden="true">
              <path
                className="memory-pin__body"
                d="M16 1C7.72 1 1 7.72 1 16c0 10.9 15 25 15 25s15-14.1 15-25C31 7.72 24.28 1 16 1Z"
              />
              <circle className="memory-pin__core" cx="16" cy="15.5" r="5.25" />
            </svg>
          </div>
        </div>
      </Html>
    </group>
  );
}

export function Markers({
  cities,
  selectedSlug,
  onSelect,
}: {
  cities: Memory[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
}) {
  const highlighted = usePinHighlight((s) => s.slug);
  const origin = usePinHighlight((s) => s.origin);

  return (
    <group>
      {cities.map((city) => (
        <Marker
          key={city.id}
          city={city}
          selected={selectedSlug === city.id}
          hovered={highlighted === city.id}
          ringed={highlighted === city.id && origin !== 'pin'}
          dimmed={selectedSlug !== null && selectedSlug !== city.id}
          onSelect={onSelect}
        />
      ))}
    </group>
  );
}
